import styled, { css } from 'styled-components';

import { ExtratoStatus } from '../../operations/extratoBancario';

type Look = { color: string; background: string; border: string; dashed?: boolean };

export const STATUS_LOOK: Record<ExtratoStatus, Look> = {
  entregue: { color: '#1E7B45', background: '#EAF6EF', border: '#9FD3B3' },
  nao_entregue: { color: '#B42318', background: '#FDECEA', border: '#F0A9A1' },
  em_conferencia: { color: '#1F5FAD', background: '#EAF1FB', border: '#A9C5EC' },
  sem_movimento: { color: '#4A5163', background: '#F2F3F5', border: '#D5D8DF' },
  pendente: { color: '#4A5163', background: '#FFFFFF', border: '#C9CDD6' },
  aberto: { color: '#6B7280', background: '#FFFFFF', border: '#C9CDD6', dashed: true },
  futuro: { color: '#9AA0AC', background: '#FAF8FD', border: '#EEE7F8' },
  fora: { color: '#9AA0AC', background: '#FAF8FD', border: '#EEE7F8' },
};

export const Container = styled.div`
  width: 100%;
`;

export const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  flex-wrap: wrap;
  gap: 16px;

  h2 {
    font-size: 22px;
    font-family: 'Roboto', sans-serif;
    font-weight: bold;
    color: var(--gray-200);
    margin-bottom: 0;
  }
`;

export const YearNav = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

export const YearButton = styled.button`
  width: 40px;
  height: 40px;
  border-radius: 8px;
  border: 1px solid #d9d9d9;
  background: #fff;
  font-size: 16px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;

  &:disabled {
    cursor: not-allowed;
    opacity: 0.4;
  }

  &:hover:not(:disabled) {
    border-color: var(--primary);
    color: var(--primary);
  }
`;

export const YearLabel = styled.span`
  min-width: 64px;
  text-align: center;
  font-weight: 700;
  font-size: 16px;
`;

export const Legend = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px 18px;
  margin-bottom: 16px;
  font-size: 12px;
  color: #4a5163;
`;

export const LegendItem = styled.span<{ $status: ExtratoStatus }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;

  &::before {
    content: '';
    width: 12px;
    height: 12px;
    border-radius: 3px;
    background: ${({ $status }) => STATUS_LOOK[$status].color};
  }
`;

export const MissingBanner = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 16px;
  padding: 12px 16px;
  border-radius: 10px;
  border: 1px solid #f0a9a1;
  background: #fff8f7;
  color: #b42318;
  font-size: 14px;

  strong {
    font-weight: 600;
  }

  span.note {
    display: block;
    font-size: 13px;
    color: #7a4100;
  }
`;

export const MonthsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(6, minmax(0, 1fr));
  gap: 12px;

  @media (max-width: 1200px) {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }

  @media (max-width: 768px) {
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 8px;
  }

  @media (max-width: 420px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
`;

export const MonthCard = styled.button<{ $status: ExtratoStatus }>`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 6px;
  min-height: 118px;
  padding: 14px;
  border-radius: 10px;
  text-align: left;
  font-family: inherit;
  cursor: pointer;
  transition: box-shadow 0.15s ease, transform 0.15s ease;

  ${({ $status }) => {
    const look = STATUS_LOOK[$status];
    return css`
      color: ${look.color};
      background: ${look.background};
      border: ${$status === 'nao_entregue' ? '2px' : '1px'} ${look.dashed ? 'dashed' : 'solid'} ${look.border};
    `;
  }}

  &:hover:not(:disabled) {
    box-shadow: 0 4px 14px rgba(61, 36, 115, 0.12);
    transform: translateY(-1px);
  }

  &:disabled {
    cursor: default;
  }

  &:focus-visible {
    outline: 2px solid var(--primary);
    outline-offset: 2px;
  }

  @media (max-width: 768px) {
    min-height: 92px;
    padding: 10px;
  }
`;

export const MonthName = styled.span`
  font-size: 15px;
  font-weight: 600;
  color: #1d2433;
  display: flex;
  justify-content: space-between;
  width: 100%;
`;

export const MonthStatus = styled.span`
  font-size: 12px;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  gap: 4px;
`;

export const MonthFiles = styled.span`
  font-size: 12px;
  color: #4a5163;
`;

export const MonthAction = styled.span<{ $strong?: boolean }>`
  margin-top: auto;
  font-size: 12px;
  font-weight: 600;
  color: ${({ $strong }) => ($strong ? '#B42318' : 'var(--primary)')};
`;

export const ModalNote = styled.div`
  margin-bottom: 16px;
  padding: 12px 14px;
  border-radius: 8px;
  background: #fff4e5;
  border: 1px solid #ffd8a8;
  color: #7a4100;
  font-size: 13px;
  line-height: 1.5;
`;

export const FieldLabel = styled.label`
  display: block;
  margin-bottom: 6px;
  font-size: 13px;
  font-weight: 600;
  color: #1d2433;

  span {
    font-weight: 400;
    color: #6b7280;
  }
`;

export const SentList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 18px;
`;

export const SentItem = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  border: 1px solid #eee7f8;
  border-radius: 8px;
  font-size: 13px;

  .info {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
  }

  .name {
    font-weight: 500;
    color: #1d2433;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .meta {
    font-size: 12px;
    color: #6b7280;
  }
`;

export const PdfBadge = styled.span`
  flex-shrink: 0;
  font-size: 10px;
  font-weight: 700;
  color: #b42318;
  border: 1px solid #f0a9a1;
  border-radius: 4px;
  padding: 2px 5px;
`;

export const LinkButton = styled.button<{ $danger?: boolean }>`
  border: 0;
  background: transparent;
  padding: 6px 4px;
  font-size: 13px;
  font-family: inherit;
  cursor: pointer;
  color: ${({ $danger }) => ($danger ? '#B42318' : 'var(--primary)')};

  &:hover {
    text-decoration: underline;
  }
`;

export const WhatsappContainer = styled.div`
  margin-top: 28px;
  padding: 18px;
  border-radius: 12px;
  background: #f3e6ff;
  border: 1px solid #e6ccff;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  flex-wrap: wrap;
  text-align: center;
`;

export const WhatsappText = styled.span`
  font-weight: 600;
  color: #3d2473;
`;

export const WhatsappLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 18px;
  border-radius: 8px;
  background: #1e9e57;
  color: #fff;
  font-weight: 600;

  &:hover {
    color: #fff;
    opacity: 0.9;
  }
`;
